import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export type JsonShape = 'any' | 'object' | 'array';

interface JsonEditorFieldProps {
  /** The committed value from the node's parameter (object, list, string, null...). */
  value: unknown;
  /** Called with the parsed value when the text is valid JSON and differs from `value`. */
  onCommit: (value: unknown) => void;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
  /** Reject values of the wrong shape with a plain message instead of saving them. */
  expectedShape?: JsonShape;
  /** When true, an empty editor commits `null` (for optional fields such as a schema). */
  emptyMeansNull?: boolean;
  /** Extra validation on the parsed value; return a message to block the commit. */
  validate?: (value: unknown) => string | null;
  'aria-label'?: string;
  'data-testid'?: string;
  className?: string;
}

const stringify = (value: unknown): string => {
  if (value === undefined) return '';
  if (typeof value === 'string') {
    // A string param may hold pre-serialised JSON; show it as the JSON it is.
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2) ?? '';
  } catch {
    return '';
  }
};

const shapeOf = (value: unknown): JsonShape =>
  Array.isArray(value)
    ? 'array'
    : value !== null && typeof value === 'object'
      ? 'object'
      : 'any';

const describeParseError = (text: string, error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error);
  const position = /position (\d+)/i.exec(message);
  if (position) {
    const offset = Number(position[1]);
    const line = text.slice(0, offset).split('\n').length;
    return `This is not valid JSON (problem near line ${line}). Check for a missing comma, quote or bracket.`;
  }
  return 'This is not valid JSON. Check for a missing comma, quote or bracket.';
};

/**
 * A JSON text editor for any parameter the backend declares as `JsonInput`.
 * The text is local while typing; it is parsed and committed on blur (or
 * Format), and an invalid document shows a plain message instead of being
 * silently dropped or reverted.
 */
export function JsonEditorField({
  value,
  onCommit,
  disabled = false,
  placeholder = 'Enter JSON',
  rows = 8,
  expectedShape = 'any',
  emptyMeansNull = false,
  validate,
  className,
  ...aria
}: JsonEditorFieldProps) {
  const [text, setText] = useState(() => stringify(value));
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Follow external changes (undo, agent edits, another control) while the
  // user is not in the middle of typing. Keyed on the value only: finishing
  // an edit must not snap the text back to the previous value.
  useEffect(() => {
    if (!dirty) setText(stringify(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const parse = (): { ok: true; value: unknown } | { ok: false } => {
    const trimmed = text.trim();
    if (trimmed === '') {
      if (emptyMeansNull) return { ok: true, value: null };
      setError(
        expectedShape === 'array'
          ? 'Enter a JSON list, for example [].'
          : expectedShape === 'object'
            ? 'Enter a JSON object, for example {}.'
            : 'Enter a JSON value.',
      );
      return { ok: false };
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch (parseError) {
      setError(describeParseError(text, parseError));
      return { ok: false };
    }
    if (expectedShape !== 'any' && shapeOf(parsed) !== expectedShape) {
      setError(
        expectedShape === 'array'
          ? 'This field needs a JSON list, for example [ ... ].'
          : 'This field needs a JSON object, for example { ... }.',
      );
      return { ok: false };
    }
    const custom = validate?.(parsed);
    if (custom) {
      setError(custom);
      return { ok: false };
    }
    setError(null);
    return { ok: true, value: parsed };
  };

  const commit = () => {
    const result = parse();
    if (!result.ok) return;
    setDirty(false);
    if (JSON.stringify(result.value) !== JSON.stringify(value)) {
      onCommit(result.value);
    }
  };

  const format = () => {
    const result = parse();
    if (!result.ok) return;
    setText(JSON.stringify(result.value, null, 2));
    setDirty(false);
    if (JSON.stringify(result.value) !== JSON.stringify(value)) {
      onCommit(result.value);
    }
  };

  return (
    <div className={cn('relative', className)}>
      <Textarea
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setDirty(true);
          if (error) setError(null);
        }}
        onBlur={commit}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        spellCheck={false}
        aria-invalid={error ? true : undefined}
        className={cn(
          'font-mono text-xs leading-5 min-h-24',
          error && 'border-red-500 focus-visible:border-red-500',
        )}
        {...aria}
      />
      <div className='mt-1 flex items-start justify-between gap-2'>
        <p
          className={cn(
            'text-xs',
            error ? 'text-red-500' : 'text-muted-foreground',
          )}
          role={error ? 'alert' : undefined}
        >
          {error ?? (dirty ? 'Saved when you leave the field.' : '')}
        </p>
        {!disabled && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-2 text-xs shrink-0'
            onMouseDown={(event) => event.preventDefault()}
            onClick={format}
          >
            Format
          </Button>
        )}
      </div>
    </div>
  );
}
